import React, { useCallback, useMemo } from 'react'

import { AlertRecipientIncompatible } from './AlertRecipientIncompatible'
import {
  AdditionalText,
  AdditionalTextAddendum,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import {
  MaterialCertificationsAlertRule,
  MATERIAL_CERTIFICATION_ALERT_RULE_TIMBER_CERT_TYPES,
  MaterialCertificationsAlertRuleTimberRequiredCertType,
  determineEnabledMaterialCertificationsAlertCount
} from './constants'
import { SwitchChangeEventHandler } from 'antd/es/switch'
import { Checkbox, Col, Radio, Row } from 'antd'
import { EnableSwitch } from './EnableSwitch'
import _ from 'lodash'
import styled from 'styled-components'

const timberOptionDescrioptions: Record<
  MaterialCertificationsAlertRuleTimberRequiredCertType,
  string
> = {
  FSC: 'FSC Certificate',
  PEFC: 'PEFC Certificate',
  FSCOrPEFC: 'FSC or PEFC Certificate'
}

const timberCertificateOptions =
  MATERIAL_CERTIFICATION_ALERT_RULE_TIMBER_CERT_TYPES.map(type => ({
    value: type,
    label: timberOptionDescrioptions[type]
  }))

export const MaterialCertifications = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}) => {
  const handleValueChange = useCallback(
    (mutation: (rule: MaterialCertificationsAlertRule) => void) => {
      const updatedAlertRules = _.cloneDeep(alertRules)
      mutation(updatedAlertRules[alertKey])
      const numEnabledOptions =
        determineEnabledMaterialCertificationsAlertCount(
          updatedAlertRules[alertKey] as MaterialCertificationsAlertRule
        )
      updatedAlertRules[alertKey].isEnabled = numEnabledOptions > 0
      setAlertRules(updatedAlertRules)
    },
    [alertKey, alertRules, setAlertRules]
  )

  const currentAlertRule: MaterialCertificationsAlertRule | null | undefined =
    useMemo(() => alertRules[alertKey], [alertKey, alertRules])

  if (!currentAlertRule) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Material Certifications</Description>
          <AdditionalText>
            Get notified when a product supplier is missing the required
            certifications for materials delivered. Choose which certifications
            are checked.
          </AdditionalText>
          <AdditionalTextAddendum>
            * Each selection constitutes one alert
          </AdditionalTextAddendum>
        </TextArea>
        <SettingArea>
          <MaterialCertificationOption
            label="Bricks &amp; Blocks"
            subLabel="BES6001 or ISO14001"
            isOptionOn={currentAlertRule.isBricksAndBlocksBESOrISOEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isBricksAndBlocksBESOrISOEnabled =
                    !rule.isBricksAndBlocksBESOrISOEnabled)
              )
            }
          />
          <MaterialCertificationOption
            label="Concrete"
            subLabel="BES6001 or ISO14001"
            isOptionOn={currentAlertRule.isConcreteBESOrISOEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isConcreteBESOrISOEnabled =
                    !rule.isConcreteBESOrISOEnabled)
              )
            }
          />
          <MaterialCertificationOption
            label="Insulation"
            subLabel="ISO14001"
            isOptionOn={currentAlertRule.isInsulationISOEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isInsulationISOEnabled = !rule.isInsulationISOEnabled)
              )
            }
          />
          <MaterialCertificationOption
            label="Plasterboard"
            subLabel="BES6001 or ISO14001"
            isOptionOn={currentAlertRule.isPlasterboardBESOrISOEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isPlasterboardBESOrISOEnabled =
                    !rule.isPlasterboardBESOrISOEnabled)
              )
            }
          />
          <MaterialCertificationOption
            label="Rebar"
            subLabel="CARES SCS"
            isOptionOn={currentAlertRule.isRebarCARESEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule => (rule.isRebarCARESEnabled = !rule.isRebarCARESEnabled)
              )
            }
          />
          <MaterialCertificationOption
            label="Structural Steel"
            subLabel="BES6001 or ISO14001"
            isOptionOn={currentAlertRule.isStructuralSteelBESOrISOEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule =>
                  (rule.isStructuralSteelBESOrISOEnabled =
                    !rule.isStructuralSteelBESOrISOEnabled)
              )
            }
          />
          <MaterialCertificationOption
            label="Timber"
            isOptionOn={currentAlertRule.isTimberEnabled}
            cannotEnable={cannotEnable}
            handleCheckedChange={() =>
              handleValueChange(
                rule => (rule.isTimberEnabled = !rule.isTimberEnabled)
              )
            }
          />
          <Row>
            <Col style={{ width: '50px' }}></Col>
            <Col>
              <Radio.Group
                name="timberRequiredCertificate"
                value={currentAlertRule.timberRequiredCertificate}
                options={timberCertificateOptions}
                disabled={!currentAlertRule.isTimberEnabled}
                onChange={e =>
                  handleValueChange(
                    rule => (rule.timberRequiredCertificate = e.target.value)
                  )
                }
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: '0.25em' }}>
            <Col style={{ width: '50px' }}></Col>
            <Col>
              <Checkbox
                name="timberClaimEvidencedOnProduct"
                title="Timber rating evidenced on product"
                checked={currentAlertRule.timberClaimEvidencedOnProduct}
                disabled={!currentAlertRule.isTimberEnabled}
                onChange={e =>
                  handleValueChange(
                    rule =>
                      (rule.timberClaimEvidencedOnProduct = e.target.checked)
                  )
                }
              >
                Timber rating evidenced on product
              </Checkbox>
            </Col>
          </Row>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipientIncompatible />
    </>
  )
}

function MaterialCertificationOption({
  label,
  subLabel,
  isOptionOn,
  cannotEnable,
  handleCheckedChange
}: {
  label: string
  subLabel?: string
  isOptionOn: boolean
  cannotEnable: boolean
  handleCheckedChange: SwitchChangeEventHandler
}) {
  const cleanedLabel = label.replace(/[ &]/g, '')
  const id = 'materialCertification' + cleanedLabel

  return (
    <Row style={{ width: '100%' }}>
      <StyleMultiLineLabelWrapper>
        <EnableSwitch
          id={id}
          checked={isOptionOn}
          disabled={!isOptionOn && cannotEnable}
          overrideLabel={null}
          ariaLabel={label}
          onChange={handleCheckedChange}
        />
        <label htmlFor={id} style={{ cursor: 'pointer' }}>
          <LabelAndSubLabel label={label} subLabel={subLabel} />
        </label>
      </StyleMultiLineLabelWrapper>
    </Row>
  )
}

function LabelAndSubLabel({
  label,
  subLabel
}: {
  label: string
  subLabel?: string
}) {
  return (
    <div>
      <div style={{ fontWeight: 600 }}>{label}</div>
      {subLabel && <div>{subLabel}</div>}
    </div>
  )
}

const StyleMultiLineLabelWrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1rem;
`
